import {
  collection,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { patchMonthlyStats } from "../services/statsService";

function toDateSafe(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toMonthKey(value) {
  const date = toDateSafe(value);
  if (!date) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function emptyStats(monthKey) {
  return {
    monthKey,
    incomeTotal: 0,
    expensesTotal: 0,
    paymentsTotal: 0,
    invoicesTotal: 0,
    invoicesPaid: 0,
    invoicesOpen: 0,
    incomeCount: 0,
    expensesCount: 0,
    paymentsCount: 0,
    invoicesCount: 0,
  };
}

function ensureMonthBucket(map, monthKey) {
  if (!monthKey) return null;
  if (!map.has(monthKey)) {
    map.set(monthKey, emptyStats(monthKey));
  }
  return map.get(monthKey);
}

function addEntry(map, monthKey, patch) {
  const bucket = ensureMonthBucket(map, monthKey);
  if (!bucket) return;

  Object.entries(patch).forEach(([key, value]) => {
    if (key === "monthKey") return;
    bucket[key] = Number(bucket[key] || 0) + Number(value || 0);
  });
}

async function clearStatsCollection() {
  const snapshot = await getDocs(collection(db, "stats"));
  const docs = snapshot.docs;

  for (let i = 0; i < docs.length; i += 400) {
    const chunk = docs.slice(i, i + 400);
    const batch = writeBatch(db);
    chunk.forEach((entry) => batch.delete(entry.ref));
    await batch.commit();
  }
}

export async function rebuildMonthlyStats(monthKey = null) {
  const [incomeSnap, expensesSnap, paymentsSnap, invoicesSnap] = await Promise.all([
    getDocs(collection(db, "income")),
    getDocs(collection(db, "expenses")),
    getDocs(collection(db, "payments")),
    getDocs(collection(db, "invoices")),
  ]);

  const statsByMonth = new Map();
  const filterMonthKey = monthKey || null;

  incomeSnap.docs.forEach((docSnap) => {
    const item = docSnap.data();
    const itemMonthKey = toMonthKey(item.date || item.createdAt);
    if (!itemMonthKey || (filterMonthKey && itemMonthKey !== filterMonthKey)) return;

    addEntry(statsByMonth, itemMonthKey, {
      incomeTotal: Number(item.amount || 0),
      incomeCount: 1,
    });
  });

  expensesSnap.docs.forEach((docSnap) => {
    const item = docSnap.data();
    const itemMonthKey = toMonthKey(item.date || item.createdAt);
    if (!itemMonthKey || (filterMonthKey && itemMonthKey !== filterMonthKey)) return;

    addEntry(statsByMonth, itemMonthKey, {
      expensesTotal: Number(item.amount || 0),
      expensesCount: 1,
    });
  });

  paymentsSnap.docs.forEach((docSnap) => {
    const item = docSnap.data();
    const itemMonthKey = toMonthKey(item.date || item.createdAt);
    if (!itemMonthKey || (filterMonthKey && itemMonthKey !== filterMonthKey)) return;

    addEntry(statsByMonth, itemMonthKey, {
      paymentsTotal: Number(item.amount || 0),
      paymentsCount: 1,
    });
  });

  invoicesSnap.docs.forEach((docSnap) => {
    const item = docSnap.data();
    const itemMonthKey = toMonthKey(item.invoiceDate || item.date || item.createdAt);
    if (!itemMonthKey || (filterMonthKey && itemMonthKey !== filterMonthKey)) return;

    const total = Number(item.grandTotal || item.total || 0);
    const paid = Number(item.paidAmount || 0);

    addEntry(statsByMonth, itemMonthKey, {
      invoicesTotal: total,
      invoicesPaid: paid,
      invoicesOpen: Math.max(0, total - paid),
      invoicesCount: 1,
    });
  });

  if (filterMonthKey && !statsByMonth.has(filterMonthKey)) {
    statsByMonth.set(filterMonthKey, emptyStats(filterMonthKey));
  }

  if (!filterMonthKey) {
    await clearStatsCollection();

    if (!statsByMonth.size) {
      const currentMonthKey = new Date().toISOString().slice(0, 7);
      statsByMonth.set(currentMonthKey, emptyStats(currentMonthKey));
    }
  }

  const orderedMonthKeys = Array.from(statsByMonth.keys()).sort();
  for (const key of orderedMonthKeys) {
    await patchMonthlyStats(key, statsByMonth.get(key));
  }

  if (filterMonthKey) {
    return statsByMonth.get(filterMonthKey) || emptyStats(filterMonthKey);
  }

  return orderedMonthKeys.map((key) => statsByMonth.get(key));
}

export default rebuildMonthlyStats;
